import { Checkbox, CheckboxProps, FormControl, HStack } from '@metservicedev/met-ui';
import React from 'react';
import { Controller, FieldPath, FieldValues, get, useFormContext } from 'react-hook-form';

import { InfoToolTip } from './InfoToolTip';

type Props<T extends FieldValues> = {
  'data-testid'?: string;
  /** Fields that should be revalidated if this one changes */
  dependants?: FieldPath<T> | FieldPath<T>[];
  disabled?: boolean;
  fieldPath: FieldPath<T>;
  helperText?: string;
  label: string;
  tooltipContent?: string;
  checkboxProps?: CheckboxProps;
  formControlProps?: React.ComponentProps<typeof FormControl>;
};

export const FormCheckbox = <T extends FieldValues>({
  'data-testid': testId,
  dependants,
  disabled,
  fieldPath,
  label,
  tooltipContent,
  checkboxProps,
  formControlProps,
}: Props<T>): React.ReactElement<Props<T>> => {
  const { formState, control } = useFormContext<T>();
  const error = get(formState.errors, fieldPath);
  return (
    <Controller
      control={control}
      name={fieldPath}
      rules={{ deps: dependants }}
      render={({ field: { onBlur, onChange, value } }) => (
        <FormControl
          {...formControlProps}
          disabled={disabled}
          invalid={!!error || undefined}
        >
          <HStack>
            <Checkbox
              marginLeft={2}
              {...checkboxProps}
              data-testid={testId}
              checked={value}
              onCheckedChange={(checked) => {
                onChange(checked.checked);
                onBlur();
              }}
            >
              {label}
            </Checkbox>
            {tooltipContent && (
              <InfoToolTip
                tooltipContent={tooltipContent}
                data-testid={`${testId}-tooltip`}
              />
            )}
          </HStack>
        </FormControl>
      )}
    />
  );
};
